import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import {
    Image as ImageIcon,
} from '@mui/icons-material'
import { Box, Typography, useTheme, Input, Button, TextField } from '@mui/material'
import { VideoNotesAnalysisState } from '../../../../features/types'
import { videonotesApiSlice } from '../../../../features/section/videonotesSlice'
import { H5 } from '../../../../layouts/TextStyles'
import ImageViewerDialog from '../../../../components/dialogs/ImageViewerDialog'

interface Props {
    videonotes: VideoNotesAnalysisState
}

const VideonotesCardSection: React.FC<Props> = ({ videonotes }) => {
    const { formatMessage } = useIntl()
    const theme = useTheme()
    const [pointInput, setPointInput] = useState<number | ''>(videonotes.point ?? '')
    const [feedbackInput, setFeedbackInput] = useState<string>(videonotes.feedback ?? '')
    const [isImageDialogOpen, setIsImageDialogOpen] = useState(false)
    const [gradeVideonotes, { isLoading: isGrading }] = videonotesApiSlice.useGradeVideonotesMutation()

    const handleOpenImages = () => {
        setIsImageDialogOpen(true)
    }

    const styles = {
        wrapper: {
        },

        filesContainer: {
            p: 2,
            backgroundColor: theme.palette.grey[50],
            borderRadius: '8px',
        },
        sectionTitle: {
            fontSize: '14px',
            fontWeight: 600,
            color: theme.palette.text.secondary,
        },
        fileItem: {
            display: 'flex',
            alignItems: 'center',
            p: 1,
            mb: 1,
            backgroundColor: 'white',
            borderRadius: '4px',
            border: `1px solid ${theme.palette.grey[200]}`,
        },
        fileIcon: {
            mr: 1,
            color: theme.palette.text.secondary,
        },
        fileLink: {
            textDecoration: 'none',
            color: theme.palette.primary.main,
            '&:hover': {
                textDecoration: 'underline',
            },
        },
        filesButton: {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            backgroundColor: 'white',
            borderRadius: '4px',
            cursor: 'pointer',
        },
        pointInput: {
            width: '100px',
            height: '40px',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.grey[200]}`,
            padding: '0 16px',
            fontSize: '14px',
            fontWeight: 500,
            color: theme.palette.text.secondary,
        },
        feedbackInput: {
            width: '100%',
            minHeight: '120px',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.grey[200]}`,
            fontSize: '14px',
            fontWeight: 500,
            color: theme.palette.text.secondary,
        },
        feedbackContainer: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            backgroundColor: theme.palette.grey[50],
            borderRadius: '8px'
        },
    }

    const handlePointChange = (value: string) => {
        if (value === '') {
            setPointInput('')
            return
        }
        const parsed = Math.floor(Number(value))
        if (Number.isNaN(parsed)) {
            return
        }
        const clamped = Math.max(0, Math.min(100, parsed))
        setPointInput(clamped)
    }

    const handleFeedbackChange = (value: string) => {
        setFeedbackInput(value)
    }

    const handleGradeVideonotes = (id: number) => {
        gradeVideonotes({
            body: {
                videoNotesId: id,
                point: typeof pointInput === 'number' ? pointInput : 0,
                feedback: feedbackInput,
            },
        })
    }

    return (
        <Box sx={styles.wrapper}>
            <Box sx={styles.filesContainer}>
                {/* Student submitted files */}
                {videonotes?.files && videonotes?.files?.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ ...styles.sectionTitle, mb: 1 }}>
                            Файлы студента:
                        </Typography>
                        <Box
                            onClick={handleOpenImages}
                            sx={styles.filesButton}
                        >
                            <ImageIcon sx={{ color: theme.palette.primary.main }} />
                            <Typography sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                                Просмотреть файлы ({videonotes.files.length})
                            </Typography>
                        </Box>
                    </Box>
                )}
                {videonotes?.feedback && (
                    <Box sx={styles.feedbackContainer}>
                        <Typography sx={styles.sectionTitle}>
                            Комментарий к заданию:
                        </Typography>
                        <Typography>
                            {videonotes?.feedback}
                        </Typography>
                    </Box>
                )}
                {!videonotes?.point && (
                    <Box sx={styles.feedbackContainer}>
                        <TextField
                            multiline
                            minRows={4}
                            maxRows={12}
                            sx={styles.feedbackInput}
                            placeholder="Введите обратную связь"
                            value={feedbackInput}
                            onChange={(e) => handleFeedbackChange(e.target.value)}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                            <H5>Баллы:</H5>
                            <Input
                                sx={styles.pointInput}
                                placeholder="Введите баллы"
                                value={pointInput}
                                type="number"
                                inputProps={{ min: 0, max: 100, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                                onKeyDown={(e) => {
                                    const invalidKeys = ['e', 'E', '+', '-', '.']
                                    if (invalidKeys.includes(e.key)) {
                                        e.preventDefault()
                                    }
                                }}
                                onChange={(e) => handlePointChange(e.target.value)}
                                onBlur={(e) => handlePointChange(e.target.value)}
                            />
                            <Button
                                sx={{ height: '40px' }}
                                variant="contained"
                                color="primary"
                                disabled={isGrading}
                                onClick={() => handleGradeVideonotes(videonotes?.id)}
                            >
                                {formatMessage({ id: 'grade_homework' })}
                            </Button>

                        </Box>
                    </Box>
                )}
            </Box>

            <ImageViewerDialog
                open={isImageDialogOpen}
                onClose={() => setIsImageDialogOpen(false)}
                files={videonotes?.files || null}
            />
        </Box>
    )
}

export default VideonotesCardSection
